/**
 * The camera following you while you navigate.
 *
 * Two things move it: your position, so the route stays under you, and your
 * heading, so up on screen is the way you are facing. Together they are what
 * separates "a map with a line on it" from navigation.
 *
 * The one rule that matters: **touching the map wins.** If you drag, pinch or
 * rotate, following stops immediately and stays stopped until you ask for it
 * again. A camera that fights the finger is worse than no camera at all, and
 * every phone map that has ever annoyed anyone got this wrong at some point.
 * Distinguishing your gesture from our own easeTo is done by looking for the
 * originating DOM event, which our moves do not have.
 */

/** Close enough to read street names, far enough to see the next turn. */
const FOLLOW_ZOOM = 17.6;

/** Leaned over, so the road ahead occupies more screen than the road behind. */
const FOLLOW_PITCH = 60;

/**
 * How long each camera correction takes.
 *
 * Matched to the GPS update rate rather than made short: a fix arrives about
 * once a second, so easing over most of that interval turns a sequence of
 * jumps into continuous motion. Shorter looks like stuttering, longer lags
 * behind where you actually are.
 */
const EASE_MS = 900;

export function createFollow({ map, compass, onChange, pitch = FOLLOW_PITCH } = {}) {
  let active = false;

  function apply(position, { frame = false } = {}) {
    if (!active || !position) return;
    const bearing = compass?.heading;

    map.easeTo({
      center: position,
      // Starting frames the road ahead at the follow zoom even if the map was
      // parked closer in — FOLLOW_ZOOM is the "see the next turn" distance,
      // and pressing Start is when that promise is made. Later fixes only
      // ever pull the camera in, never out from where you left it.
      zoom: frame ? FOLLOW_ZOOM : Math.max(map.getZoom(), FOLLOW_ZOOM),
      pitch,
      ...(Number.isFinite(bearing) ? { bearing } : {}),
      duration: EASE_MS,
      // Without this the pan is treated as a gesture by MapLibre's own
      // inertia handling, which then fights the next correction.
      essential: true,
    });
  }

  /**
   * A gesture is a move that carries a DOM event. Ours carry none.
   *
   * That test is the whole discrimination, and it is deliberately the only
   * one. A "we are currently easing" flag looks like a useful second guard
   * and is actively harmful: a fix arrives about once a second and each ease
   * runs for 900ms, so the flag is set essentially always, and the one moment
   * it matters — you grabbing the map mid-ease, which is when you would grab
   * it — is exactly the moment it would swallow the gesture.
   */
  const interrupted = (event) => {
    if (!active) return;
    if (event?.originalEvent) stop();
  };
  map.on("dragstart", interrupted);
  map.on("rotatestart", interrupted);
  map.on("pitchstart", interrupted);
  map.on("zoomstart", interrupted);

  function stop() {
    if (!active) return;
    active = false;
    compass?.stop();
    onChange?.(false);
  }

  return {
    get active() { return active; },

    /**
     * Begin following. Called from the Start button, which is also the user
     * gesture iOS requires before it will hand over the magnetometer.
     */
    async start(position) {
      if (active) return;
      active = true;
      await compass?.start();
      apply(position, { frame: true });
      onChange?.(true);
    },

    stop,

    toggle(position) {
      return active ? stop() : this.start(position);
    },

    /** New fix from the position watch. */
    onPosition(position) {
      apply(position);
    },

    /** New bearing from the compass, between fixes. */
    onHeading(position) {
      apply(position);
    },
  };
}
